/**
 * Support desk — superuser only. Every problem/change report with its
 * screenshot, AI triage (summary, priority, area, suggested fix), status
 * control, an AI-drafted reply (Claude — edit before sending), respond by
 * email, re-run triage, and delete. The person keeps the final say on every
 * action; AI only drafts and triages.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
// Vault's own prices and revenue are rand (Paystack), whatever the account's currency.
import { fmtZAR as fmtR } from '../money.js';
import { api, tokens } from '../api.js';
import { ask } from '../components/Ask.jsx';
import AIAction from '../components/AIAction.jsx';

/** Replay of a recorded web journey: the click/navigate timeline the reporter
 * walked, to follow along while their voice note plays. */
function JourneyTimeline({ events }) {
  const mmss = (ms) => {
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  const icon = { start: '▶', click: '👆', navigate: '➡', page: '↻' };
  return (
    <div className="mt-2 border border-edge rounded-xl p-3 max-h-56 overflow-y-auto">
      <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
        Journey — follow along with the voice note
      </p>
      {events.map((e, i) => (
        <div key={i} className="flex gap-2 text-xs py-0.5">
          <span className="text-slate-600 w-9 text-right shrink-0">{mmss(e.t || 0)}</span>
          <span>{icon[e.type] || '·'}</span>
          <span className="text-slate-300 truncate">
            {e.type === 'click' ? (e.target || 'click') : (e.url || e.type)}
            {e.type === 'click' && e.url ? <span className="text-slate-600"> · {e.url}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

const STATUSES = ['new', 'in_progress', 'resolved', 'wont_do'];

/** Report media through the authed API (nginx denies /media/): image, screen
 * recording or voice note, fetched as a blob with the JWT. */
function AuthedMedia({ id, kind, name }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    fetch(`/api/feedback/journeys/${id}/${kind}/`,
          { headers: { Authorization: `Bearer ${tokens.access}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => b && setUrl(URL.createObjectURL(b)))
      .catch(() => {});
  }, [id, kind]);
  if (!url) return null;
  if (kind === 'video') return <video src={url} controls className="w-64 rounded border border-edge" />;
  if (kind === 'audio') return <audio src={url} controls className="w-64" />;
  // Support Attachments standard: any-type file, offered as a download.
  if (kind === 'attachment') return (
    <a href={url} download={name || 'attachment'}
       className="text-sm text-vblue underline hover:text-white">⬇ Download attachment</a>
  );
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="" className="w-40 rounded border border-edge hover:border-vblue transition" />
    </a>
  );
}

export default function Support() {
  const [reports, setReports] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [kindFilter, setKindFilter] = useState('all'); // all | problem | change

  const load = useCallback(() => {
    api('/feedback/journeys/').then((d) => setReports(d.results ?? d)).catch((e) => setError(e.message));
    api('/billing/revenue/').then(setRevenue).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    try { await api(path, { method: 'POST', body }); load(); }
    catch (e) { setError(e.data?.detail || e.message); }
  };

  const draftBefore = useRef('');   // pre-AI draft, so AIAction's Undo restores it
  const fetchDraft = (id) =>
    api(`/feedback/journeys/${id}/respond/`, { method: 'POST', body: { ai_draft: 1 } })
      .then((d) => d.draft || null);

  const send = async (id) => {
    await act(`/feedback/journeys/${id}/respond/`, { body: draft, status: 'in_progress' });
    setDraft(''); setOpenId(null);
  };

  const remove = async (id) => {
    if (!await ask({ title: 'Delete this report for good?',
                     message: 'Media and replies go with it.',
                     ok: 'Delete', danger: true, cancel: 'Keep it' })) return;
    try { await api(`/feedback/journeys/${id}/`, { method: 'DELETE' }); load(); }
    catch (e) { setError(e.message); }
  };

  return (
    <div className="min-h-full w-full max-w-screen-2xl mx-auto px-6 md:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/app" className="text-slate-500 hover:text-white text-xl" title="Back to the app">←</Link>
        <h1 className="text-2xl font-bold flex-1">Owner desk</h1>
        <button className="text-sm border border-edge rounded-xl px-3 py-1.5 hover:border-vpurple"
                title="Every report as txt + screenshots + voice, zipped for a Claude session to evaluate and action"
                onClick={async () => {
                  const r = await fetch('/api/feedback/journeys/export_zip/',
                    { headers: { Authorization: `Bearer ${tokens.access}` } });
                  const blob = await r.blob();
                  const stamp = new Date().toISOString().slice(0, 19)
                    .replace('T', '-').replace(/:/g, '');
                  const a = Object.assign(document.createElement('a'),
                    { href: URL.createObjectURL(blob),
                      download: `vault-reports-${stamp}.zip` });
                  a.click();
                }}>
          ⬇ Export for Claude (.zip)
        </button>
        <Link to="/testing"
              className="text-sm border border-edge rounded-xl px-3 py-1.5 hover:border-vblue"
              title="The scope wizard — run the test catalog, pass/fail per test">
          Scope
        </Link>
      </div>
      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

      {revenue && (
        <section className="mb-8">
          <h2 className="font-bold mb-3">Vault income</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="card text-center">
              <div className="text-slate-400 text-xs uppercase tracking-wide">Monthly income</div>
              <div className="text-3xl font-extrabold mt-1 bg-vault-gradient bg-clip-text text-transparent">
                {fmtR(revenue.monthly_income)}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {revenue.subscribers.active} subscriber{revenue.subscribers.active === 1 ? '' : 's'} × {fmtR(revenue.plan_amount)}
              </div>
            </div>
            <div className="card text-center">
              <div className="text-slate-400 text-xs uppercase tracking-wide">Possible earnings</div>
              <div className="text-3xl font-extrabold mt-1">{fmtR(revenue.possible_earnings)}</div>
              <div className="text-xs text-slate-500 mt-1">
                + {revenue.trialing_with_card} trial{revenue.trialing_with_card === 1 ? '' : 's'} with a card on file
              </div>
            </div>
            <div className="card text-center">
              <div className="text-slate-400 text-xs uppercase tracking-wide">Payments made</div>
              <div className="text-3xl font-extrabold mt-1 text-emerald-400">
                {fmtR(revenue.payments_made_this_month)}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {revenue.payments_made_count_this_month} this month · {fmtR(revenue.payments_made_total)} all-time
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-600 mt-2">{revenue.note}</p>
        </section>
      )}

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h2 className="font-bold">Support &amp; change requests</h2>
        <div className="flex gap-1.5">
          {[['all', 'Everything'], ['problem', 'Problems'], ['change', 'Change requests']].map(([k, label]) => (
            <button key={k}
                    className={`text-xs px-2.5 py-1 rounded-full border ${
                      kindFilter === k ? 'border-vblue bg-vblue/15' : 'border-edge text-slate-500'}`}
                    onClick={() => setKindFilter(k)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-3">
        {reports.filter((r) => kindFilter === 'all' || r.kind === kindFilter).map((r) => (
          <div key={r.id} className="card">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${
                    r.kind === 'problem' ? 'border-red-400/50 text-red-300' : 'border-vblue/50 text-vblue'}`}>
                    {r.kind}
                  </span>
                  <span className="text-xs text-slate-500">#{r.id} · {r.surface} · {r.status}</span>
                  {r.ai_priority && (
                    <span className="text-[10px] uppercase rounded px-1.5 py-0.5 border border-vpurple/50 text-vpurple">
                      {r.ai_priority}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm">{r.ai_summary || r.description?.slice(0, 140) || r.page_url}</p>
                {r.ai_suggestion && (
                  <p className="mt-1 text-xs text-slate-400">AI: {r.ai_suggestion.slice(0, 200)}</p>
                )}
                <p className="mt-1 text-xs text-slate-600">
                  {r.reporter_email || 'anonymous'} · {new Date(r.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                {r.screenshot && <AuthedMedia id={r.id} kind="screenshot" />}
                {r.video && <AuthedMedia id={r.id} kind="video" />}
                {r.audio && <AuthedMedia id={r.id} kind="audio" />}
                {r.attachment && <AuthedMedia id={r.id} kind="attachment"
                                              name={String(r.attachment).split('/').pop()} />}
              </div>
            </div>

            {/* Full description + the stored reply thread — what the reporter
                sees under "My requests", visible here too. */}
            {r.description && r.description.length > 140 && (
              <p className="mt-2 text-sm text-slate-300 whitespace-pre-wrap">{r.description}</p>
            )}
            {(r.frames || []).length > 1 && (
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                {r.frames.map((f, i) => (
                  <AuthedMedia key={i} id={r.id} kind={`frame/${i}`} />
                ))}
              </div>
            )}
            {(r.events || []).length > 1 && <JourneyTimeline events={r.events} />}
            {(r.responses || []).map((resp, i) => (
              <div key={i} className="mt-2 border-l-2 border-vpurple/60 pl-3">
                <p className="text-sm text-slate-300">{resp.body}</p>
                <p className="text-[11px] text-slate-600 mt-0.5">
                  you replied · {new Date(resp.at).toLocaleString()}
                </p>
              </div>
            ))}

            <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
              <select className="input !w-auto !py-1.5" value={r.status}
                      onChange={(e) => api(`/feedback/journeys/${r.id}/`,
                        { method: 'PATCH', body: { status: e.target.value } }).then(load)}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <button className="border border-edge rounded-xl px-3 py-1.5 hover:border-vpurple"
                      onClick={() => { setOpenId(openId === r.id ? null : r.id); setDraft(''); }}>
                Respond
              </button>
              <button className="border border-edge rounded-xl px-3 py-1.5 hover:border-vblue"
                      onClick={() => act(`/feedback/journeys/${r.id}/ai_triage/`)}>
                Re-run AI triage
              </button>
              <button className="border border-edge rounded-xl px-3 py-1.5 text-red-400 hover:border-red-400"
                      onClick={() => remove(r.id)}>
                Delete
              </button>
            </div>

            {openId === r.id && (
              <div className="mt-3 border-t border-edge pt-3">
                <div className="flex gap-2 mb-2">
                  <AIAction size="sm" label="Draft with AI"
                            title="Draft a reply with AI"
                            run={() => { draftBefore.current = draft; return fetchDraft(r.id); }}
                            onResult={setDraft}
                            onUndo={() => setDraft(draftBefore.current)}
                            readback="Drafted a reply — edit before you send." />
                </div>
                <textarea className="input min-h-[110px]" value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          placeholder={`Reply to ${r.reporter_email || 'the reporter'}…`} />
                <button className="btn-primary mt-2" disabled={!draft.trim()} onClick={() => send(r.id)}>
                  Send reply {r.reporter_email ? `to ${r.reporter_email}` : '(no email — status only)'}
                </button>
              </div>
            )}
          </div>
        ))}
        {!reports.length && <p className="text-slate-500">No reports yet.</p>}
      </div>
    </div>
  );
}
